import React, { useState } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { TEXT_STYLES, TextVariant } from '@components/shared/design/Typography';
import Video from '@components/VideoModule';
import FrameImage from '@images/picture-frame.svg';
import PlayImage from '@images/play.svg';
import handleTextReplacement from '@utils/handleTextReplacement';
import useReplacedInnerHtml from '@utils/useReplacedInnerHtml';
import Colors from './Colors';
import useDesktop from './useDesktop';

interface VideoProp {
  video: {
    url?: string;
  };
  thumbnail: {
    gatsbyImageData: any;
  };
  text: {
    text: string;
  };
  bottom_text: string;
  video_width: {
    text: string;
  };
  video_height: {
    text: string;
  };
}

interface Props {
  video: VideoProp;
  hasFrame?: boolean;
  gap: string;
  textColor: string;
  textAlign: string;
  roundedCorners: boolean;
}

const VideoWithThumbnail: React.FC<Props> = ({ video, hasFrame = false, gap, textColor, textAlign, roundedCorners }) => {
  const isDesktop = useDesktop(768);
  const videoTextHtml = useReplacedInnerHtml(video.text.text);
  const autoplay = !video.thumbnail.gatsbyImageData;
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const width = isDesktop ? video.video_width?.text || '360' : '250';
  const height = isDesktop ? video.video_height?.text || '360' : '250';
  const textWidth = isDesktop ? Number(width) + 50 : Number(width);
  const frameWidth = Number(width) + 10;
  const frameHeight = Number(height) + 70;
  const isYoutube = video.video.url?.includes('youtube');

  return (
    <Container $gap={gap} $roundedCorners={roundedCorners}>
      {isPlaying ? (
        <>
          {isYoutube ? (
            <iframe width={width} height={height} src={`${video.video.url}`} title="YouTube video player" />
          ) : (
            <video width={width} height={height} controls={false} autoPlay={true} loop={true} muted={true} playsInline>
              <source src={video.video.url} type="video/mp4" />
            </video>
          )}
          {hasFrame && <FrameStyled $gap={gap} $width={frameWidth} $height={frameHeight} />}
        </>
      ) : (
        <div onClick={() => setIsPlaying(true)}>
          <Thumbnail image={video.thumbnail.gatsbyImageData} alt={''} objectFit="contain" $width={width} $height={height} />
          <PlayStyled />
        </div>
      )}
      {!!video?.text?.text && (
        <MainText $width={textWidth} $textAlign={textAlign} $textColor={textColor} dangerouslySetInnerHTML={{ __html: videoTextHtml }}></MainText>
      )}
      {video?.bottom_text && <BottomText $width={textWidth}>{video.bottom_text}</BottomText>}
    </Container>
  );
};

export default VideoWithThumbnail;

interface StyleProps {
  $gap?: string;
  $width: string | number;
  $height?: string | number;
  $textColor?: string;
  $textAlign?: string;
  $roundedCorners?: boolean;
}

const Container = styled.div<StyleProps>`
  position: relative;
  margin-top: 30px;
  padding-left: ${(props) => props.$gap};
  padding-right: ${(props) => props.$gap};

  video {
    border-radius: ${(props) => (props.$roundedCorners ? '12px' : '0')};
    object-fit: ${(props) => (props.$roundedCorners ? 'cover' : 'contain')};
  }
`;

const MainText = styled.p<StyleProps>`
  ${TEXT_STYLES[TextVariant.BodyRegularLG]};
  text-align: ${(props) => (props.$textAlign ? props.$textAlign : 'center')};
  margin-top: 40px;
  margin-left: -25px;
  color: ${(props) => (props.$textColor ? props.$textColor : Colors.forestGreen)};
  max-width: ${(props) => props.$width}px;

  @media (max-width: 768px) {
    font-size: 18px;
    margin-left: 0;
  }
`;

const PlayStyled = styled(PlayImage)`
  position: absolute;
  width: 57px;
  height: 60px;
  left: calc(50% - 57px / 2);
  top: calc(50% - 60px / 2);
`;

const FrameStyled = styled(FrameImage)<StyleProps>`
  position: absolute;
  width: ${(props) => props.$width}px;
  height: ${(props) => props.$height}px;
  left: calc(${(props) => props.$gap} - 2px);
  top: -30px;
`;

const Thumbnail = styled(GatsbyImage)<StyleProps>`
  width: ${(props) => props.$width}px;
  height: ${(props) => props.$height}px;
  border-radius: ${(props) => (props.$roundedCorners ? '12px' : '0')};
`;

const BottomText = styled.p<StyleProps>`
  ${TEXT_STYLES[TextVariant.BodyBoldLG]};
  max-width: ${(props) => props.$width}px;
  margin-top: 16px;
  margin-left: -25px;
`;
